import t from 'react-translate';
import moment from 'moment';
import { config } from '@config/pendo.config.json';

// Styles
import { css } from '@emotion/react';
import { black, gray2, gray3, gray4 } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { textMediumFontSize, semiBoldFontWeight } from 'styles/global_defaults/fonts';

// Components
import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import NvTooltip from 'shared/components/nv-tooltip';

import { useAvailabilityCalendarContext } from './availability-calendar-context';

const TOOLTIP_FORMAT_DATE = 'ddd, MMMM D';
const TOOLTIP_Z_INDEX = 5091;

const styles = css`
  .btn-today {
    border: 1px solid ${gray4};
    border-radius: 100px;
    padding: 3px ${halfSpacing}px !important;
    font-size: ${textMediumFontSize}px;
    font-weight: ${semiBoldFontWeight};
    color: ${black};
    &:hover, &:focus {
      border-color: ${gray2};
    }
  }
  .btn-previous, .btn-next {
    color: ${gray3};
    padding-left: 0;
    padding-right: 0;
    &:hover, &:focus {
      color: ${gray2};
    }
  }
  .btn-previous {
    margin-left: ${halfSpacing}px;
    margin-right: ${halfSpacing}px;
  }
  .availability-calendar-button {
    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
      color: ${gray4};
    }
  }
`;

type AvailabilityCalendarButton = {
  label?: string,
  icon?: string,
  tooltip: string,
  onClick: string,
  enabled?: string,
};

const availabilityCalendarButtons: Record<string, AvailabilityCalendarButton> = {
  today: {
    label: 'TODAY',
    onClick: 'setToday',
    tooltip: moment().format(TOOLTIP_FORMAT_DATE),
  },
  previous: {
    icon: 'arrow-left',
    onClick: 'setPreviousWeek',
    tooltip: 'PREVIOUS_WEEK',
    enabled: 'canGoToPreviousWeek',
  },
  next: {
    icon: 'arrow-right',
    onClick: 'setNextWeek',
    tooltip: 'NEXT_WEEK',
    enabled: 'canGoToNextWeek',
  },
};

type ButtonComponentProps = AvailabilityCalendarButton & {
  type: string;
};

const ButtonComponent = (props: ButtonComponentProps) => {
  const {
    type,
    label,
    icon,
    tooltip,
    enabled: enabledKey,
    onClick: onClickKey,
  } = props;
  const availabilityCalendarContext = useAvailabilityCalendarContext();
  const translations = (
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.SCHEDULING_WITH_AVAILABILITY.AVAILABILITY_CALENDAR.HEADER
  );
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.schedulingWithAvailability;

  const tooltipText = translations[tooltip]?.() || tooltip;
  const onClick = availabilityCalendarContext[onClickKey];
  const content = label ? translations[label]() : <NvIcon icon={icon} size='smallest' />;
  const isDisabled = enabledKey ? !availabilityCalendarContext[enabledKey] : false;

  return (
    <NvTooltip
      enabled={!isDisabled}
      text={tooltipText}
      placement='top'
      className='availability-calendar-button'
      zIndex={TOOLTIP_Z_INDEX}
    >
      <ClickableContainer
        onClick={onClick}
        disabled={isDisabled}
        className={`btn btn-${type} align-items-center d-flex justify-content-center py-0 ${isDisabled ? 'disabled' : ''}`}
        data-qa={dataQATag[type]}
        aria-label={translations[tooltip]?.() || translations[label]?.() || tooltip}
      >
        {content}
      </ClickableContainer>
    </NvTooltip>
  );
};

const AvailabilityCalendarButtons = () => (
  <div css={styles} className='d-flex buttons-stack'>
    {Object.keys(availabilityCalendarButtons).map((key) => (
      <ButtonComponent
        key={key}
        type={key}
        {...availabilityCalendarButtons[key]}
      />
    ))}
  </div>
);

export default AvailabilityCalendarButtons;
